import { Link } from 'react-router-dom';
import { Badge, cx } from '@/components/ui';
import { formatDay } from '@/lib/format';
import {
  STATUS_META,
  daysLeftText,
  useMyLicense,
  type EngineLicense,
  type LicenseStatus,
} from '@/lib/license';

export function LicenseStatusBadge({ status }: { status: LicenseStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.not_enforced;
  return (
    <Badge tone={meta.tone} dot>
      {meta.label}
    </Badge>
  );
}

/**
 * Channels in use against channels bought, for one engine.
 *
 * Amber from 80%, red when full: a full pool is the dialer's calls being
 * refused, which is the one thing on this screen an operator has to act on.
 */
export function ChannelMeter({ engine, compact }: { engine: EngineLicense; compact?: boolean }) {
  const { channels, in_use } = engine;
  const ratio = channels > 0 ? Math.min(1, in_use / channels) : 0;
  const tone =
    channels === 0
      ? 'bg-ink-300 dark:bg-ink-600'
      : ratio >= 1
        ? 'bg-red-500'
        : ratio >= 0.8
          ? 'bg-amber-500'
          : 'bg-brand-500';
  return (
    <div className={cx('min-w-0', compact ? 'space-y-1' : 'space-y-1.5')}>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-ink-700 dark:text-ink-200">{engine.label}</span>
        <span className="tabular-nums text-ink-500 dark:text-ink-400">
          {channels === 0 ? 'Not licensed' : `${in_use} / ${channels} in use`}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-sunken">
        <div
          className={cx('h-full rounded-full transition-[width] duration-500', tone)}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Shown across the client portal when the licence needs attention.
 *
 * Silent when all is well and in the platform portal. Expired is said in red
 * because it means calls are being refused right now -- the dialer is still
 * dialling, and every lead it hands over is going back unspoken to.
 */
export function LicenseBanner() {
  const { data } = useMyLicense();
  if (!data || !data.enforced) return null;
  if (!['expiring', 'grace', 'expired'].includes(data.status)) return null;

  const expired = data.status === 'expired';
  const grace = data.status === 'grace';
  const tone = expired || grace
    ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200'
    : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200';

  const message = expired
    ? `Your licence expired on ${formatDay(data.expires_at)}. New calls are not being answered until it is renewed.`
    : grace
      ? `Your licence expired on ${formatDay(data.expires_at)}. Calls continue until ${formatDay(data.grace_ends_at)}, then stop.`
      : `Your licence renews on ${formatDay(data.expires_at)} (${daysLeftText(data)}).`;

  return (
    <div className={cx('mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm', tone)}>
      <span className="font-medium">{message}</span>
      <Link to="/subscription" className="text-xs font-semibold underline underline-offset-2">
        View subscription
      </Link>
    </div>
  );
}
